import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useStudio } from "@/store/StudioContext";
import MediaFrame from "./MediaFrame";
import { EmptyState } from "./States";
import { t } from "@/i18n";
import { Upload, FolderOpen, RefreshCw, AlertCircle } from "lucide-react";

// multi=false â†’ comportamiento original (clic cierra inmediatamente) o con confirmLabel (selecciona y confirma)
// multi=true  â†’ selecciÃ³n mÃºltiple con botÃ³n "AÃ±adir"; onPick recibe array de assets
const AssetPicker = ({
  open,
  onOpenChange,
  onPick,
  filter = (a) => a.type === "image",
  title = t.characters.pickFromGallery,
  multi = false,
  maxSelect = 10,
  alreadySelected = [],
  confirmLabel = null,
  assets: propAssets = null,
  // Nuevas propiedades para selector de medios de escena:
  allowScopeToggle = false,
  projectAssets = null,
  allAssets = null,
  defaultScope = "project",
  onUploadClick = null,
  uploadState = null,
  onRetryUpload = null,
  onRetryAssign = null,
}) => {
  const { state } = useStudio();
  const fileInputRef = useRef(null);

  // Ãmbito: "project" (Este proyecto) o "all" (Toda mi galerÃ­a)
  const [scope, setScope] = useState(defaultScope);
  // Filtro de tipo: "all" (Todos los compatibles), "image" (ImÃ¡genes), "video" (VÃ­deos)
  const [typeFilter, setTypeFilter] = useState("all");
  const [q, setQ] = useState("");
  const [multiSel, setMultiSel] = useState([]);
  const [singleSel, setSingleSel] = useState(null);

  // Limpiar selecciÃ³n al abrir
  useEffect(() => {
    if (open) {
      setMultiSel([]);
      setSingleSel(null);
      setScope(defaultScope);
      setTypeFilter("all");
      setQ("");
    }
  }, [open, defaultScope]);

  // Determinar conjunto de activos segÃºn Ã¡mbito
  let rawSource;
  if (allowScopeToggle) {
    if (scope === "all") {
      rawSource = allAssets || state.assets || [];
    } else {
      rawSource = projectAssets || propAssets || state.assets || [];
    }
  } else {
    rawSource = propAssets || state.assets || [];
  }

  // Filtrado base por compatibilidad de medio
  const baseFiltered = rawSource.filter(filter);

  // Filtrado por tipo (todos / imagen / vÃ­deo) si aplica
  const typeFiltered = baseFiltered.filter((a) => {
    if (typeFilter === "image") return a.type === "image";
    if (typeFilter === "video") return a.type === "video";
    return true;
  });

  // BÃºsqueda por texto (nombre, etiqueta o ID)
  const list = typeFiltered.filter((a) => {
    const query = q.toLowerCase();
    const labelMatch = (a.label || "").toLowerCase().includes(query);
    const filenameMatch = (a.filename || "").toLowerCase().includes(query);
    const idMatch = (a.id || "").toLowerCase().includes(query);
    return labelMatch || filenameMatch || idMatch;
  });

  const toggleItem = (id) => {
    setMultiSel((s) =>
      s.includes(id) ? s.filter((i) => i !== id) : s.length < maxSelect ? [...s, id] : s
    );
  };

  const handleSingleClick = (a) => {
    if (confirmLabel) {
      setSingleSel(a.id);
    } else {
      onPick(a);
      onOpenChange(false);
    }
  };

  const handleLocalFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file && onUploadClick) {
      onUploadClick(file);
    }
    e.target.value = "";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="asset-picker" className="max-w-3xl border-[#303030] bg-[#1b1b1b] text-[#f4f1ec]">
        <DialogHeader>
          <div className="flex flex-wrap items-center justify-between gap-2 pr-6">
            <DialogTitle className="font-display">{title}</DialogTitle>
            {onUploadClick && (
              <div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handleLocalFileInputChange}
                  data-testid="asset-picker-file-input"
                />
                <button
                  type="button"
                  data-testid="asset-picker-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  className="v-btn-primary v-btn-sm flex items-center gap-1.5 text-xs"
                >
                  <Upload size={13} />
                  <span>{t.storyboard?.uploadFromDevice || "Subir desde mi dispositivo"}</span>
                </button>
              </div>
            )}
          </div>
        </DialogHeader>

        {/* â”€â”€ Banner de progreso o error de subida/asignaciÃ³n â”€â”€ */}
        {uploadState?.isUploading && (
          <div
            data-testid="asset-picker-upload-progress"
            className="rounded border border-[#f5b544]/40 bg-[#f5b544]/10 p-2.5 text-xs text-[#f5b544]"
          >
            <div className="flex items-center justify-between mb-1.5 font-medium">
              <span>{t.storyboard?.uploadProgress?.(uploadState.progress || 0) || `Subiendo... ${uploadState.progress || 0}%`}</span>
              <span>{uploadState.progress || 0}%</span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#303030]">
              <div
                className="h-full bg-[#f5b544] transition-all duration-200"
                style={{ width: `${uploadState.progress || 0}%` }}
              />
            </div>
          </div>
        )}

        {uploadState?.error && (
          <div
            data-testid="asset-picker-upload-error"
            className="flex items-center justify-between gap-3 rounded border border-red-500/40 bg-red-500/10 p-2.5 text-xs text-red-200"
          >
            <div className="flex items-center gap-2">
              <AlertCircle size={15} className="shrink-0 text-red-400" />
              <span>{uploadState.error}</span>
            </div>
            {uploadState.step === "error_upload" && onRetryUpload && (
              <button
                type="button"
                data-testid="asset-picker-upload-retry"
                onClick={onRetryUpload}
                className="v-btn-ghost v-btn-sm shrink-0 border border-red-500/50 text-red-200 hover:bg-red-500/20"
              >
                <RefreshCw size={12} className="mr-1" />
                <span>{t.storyboard?.retryUpload || "Reintentar subida"}</span>
              </button>
            )}
            {uploadState.step === "error_assign" && onRetryAssign && (
              <button
                type="button"
                data-testid="asset-picker-assign-retry"
                onClick={onRetryAssign}
                className="v-btn-ghost v-btn-sm shrink-0 border border-red-500/50 text-red-200 hover:bg-red-500/20"
              >
                <RefreshCw size={12} className="mr-1" />
                <span>{t.storyboard?.retryAssign || "Reintentar asignaciÃ³n"}</span>
              </button>
            )}
          </div>
        )}

        {/* â”€â”€ Controles de Ãmbito (Â«Este proyectoÂ» / Â«Toda mi galerÃ­aÂ») y Filtros â”€â”€ */}
        <div className="flex flex-col gap-2.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            {allowScopeToggle && (
              <div className="flex items-center gap-1 rounded bg-[#141414] p-1 border border-[#2a2a2a]">
                <button
                  type="button"
                  data-testid="asset-picker-scope-project"
                  onClick={() => setScope("project")}
                  className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                    scope === "project"
                      ? "bg-[#282828] text-[#f5b544] shadow-sm"
                      : "text-[#a3a3a3] hover:text-[#f4f1ec]"
                  }`}
                >
                  <FolderOpen size={12} className="inline mr-1 -mt-0.5" />
                  <span>{t.storyboard?.scopeThisProject || "Este proyecto"}</span>
                </button>
                <button
                  type="button"
                  data-testid="asset-picker-scope-all"
                  onClick={() => setScope("all")}
                  className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                    scope === "all"
                      ? "bg-[#282828] text-[#f5b544] shadow-sm"
                      : "text-[#a3a3a3] hover:text-[#f4f1ec]"
                  }`}
                >
                  <span>{t.storyboard?.scopeAllGallery || "Toda mi galerÃ­a"}</span>
                </button>
              </div>
            )}

            {/* Filtros de tipo de medio */}
            <div className="flex items-center gap-1 text-xs text-[#a3a3a3]">
              <button
                type="button"
                data-testid="asset-picker-filter-all"
                onClick={() => setTypeFilter("all")}
                className={`rounded px-2 py-0.5 transition-colors ${
                  typeFilter === "all" ? "bg-[#333] text-[#f4f1ec]" : "hover:text-[#f4f1ec]"
                }`}
              >
                {t.storyboard?.filterAllMedia || "Todos"}
              </button>
              <button
                type="button"
                data-testid="asset-picker-filter-image"
                onClick={() => setTypeFilter("image")}
                className={`rounded px-2 py-0.5 transition-colors ${
                  typeFilter === "image" ? "bg-[#333] text-[#f4f1ec]" : "hover:text-[#f4f1ec]"
                }`}
              >
                {t.storyboard?.filterImages || "ImÃ¡genes"}
              </button>
              <button
                type="button"
                data-testid="asset-picker-filter-video"
                onClick={() => setTypeFilter("video")}
                className={`rounded px-2 py-0.5 transition-colors ${
                  typeFilter === "video" ? "bg-[#333] text-[#f4f1ec]" : "hover:text-[#f4f1ec]"
                }`}
              >
                {t.storyboard?.filterVideos || "VÃ­deos"}
              </button>
            </div>
          </div>

          <input
            data-testid="asset-picker-search"
            className="v-input"
            placeholder={t.common.search}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {list.length === 0 ? (
          <EmptyState title={t.gallery.empty} body={t.gallery.emptyBody} testId="asset-picker-empty" />
        ) : (
          <div className="grid max-h-[50vh] grid-cols-2 gap-3 overflow-y-auto p-1 sm:grid-cols-3 md:grid-cols-4">
            {list.map((a) => {
              if (multi) {
                const alreadyIn = alreadySelected.includes(a.id);
                const isSel = multiSel.includes(a.id);
                const atLimit = multiSel.length >= maxSelect && !isSel;
                return (
                  <button
                    key={a.id}
                    data-testid={`asset-picker-item-${a.id}`}
                    disabled={alreadyIn || atLimit}
                    onClick={() => toggleItem(a.id)}
                    className={`group relative text-left ${alreadyIn || atLimit ? "opacity-40 cursor-not-allowed" : ""}`}
                  >
                    <div className="relative">
                      <MediaFrame
                        asset={a}
                        fit="contain"
                        className={`aspect-square transition-[border-color] bg-[#121212] ${isSel ? "border-[#f5b544]" : "group-hover:border-[#f5b544]"}`}
                      />
                      {a.type === "video" && (
                        <span className="pointer-events-none absolute left-1.5 top-1.5 flex items-center gap-0.5 rounded bg-[#101010]/80 px-1 py-0.5 text-[9px] text-[#f4f1ec]">
                          â–¶ VÃ­deo
                        </span>
                      )}
                    </div>
                    <div className="mt-1 truncate text-xs text-[#a3a3a3]">{a.label || a.filename || a.id}</div>
                    {isSel && <div className="mt-0.5 text-[10px] text-[#f5b544]">Seleccionada</div>}
                  </button>
                );
              }
              // Modo single (con o sin confirmLabel)
              const isSel = singleSel === a.id;
              return (
                <button
                  key={a.id}
                  data-testid={`asset-picker-item-${a.id}`}
                  onClick={() => handleSingleClick(a)}
                  onDoubleClick={() => {
                    onPick(a);
                    onOpenChange(false);
                  }}
                  className="group relative text-left"
                >
                  <div className="relative">
                    <MediaFrame
                      asset={a}
                      fit="contain"
                      className={`aspect-square transition-[border-color] bg-[#121212] ${
                        isSel ? "border-[#f5b544]" : "group-hover:border-[#f5b544]"
                      }`}
                    />
                    {a.type === "video" && (
                      <span className="pointer-events-none absolute left-1.5 top-1.5 flex items-center gap-0.5 rounded bg-[#101010]/80 px-1 py-0.5 text-[9px] text-[#f4f1ec]">
                        â–¶ VÃ­deo
                      </span>
                    )}
                    {isSel && (
                      <div className="absolute right-1 top-1 rounded bg-[#f5b544] px-1 py-0.5 text-[9px] font-semibold text-black">
                        {t.storyboard?.selectedMediaBadge || "Seleccionado"}
                      </div>
                    )}
                  </div>
                  <div className="mt-1 truncate text-xs text-[#a3a3a3]">{a.label || a.filename || a.id}</div>
                </button>
              );
            })}
          </div>
        )}
        {multi && (
          <div className="flex items-center justify-between border-t border-[#303030] pt-3">
            <span className="text-xs text-[#a3a3a3]">
              {multiSel.length}/{maxSelect} seleccionadas
            </span>
            <button
              data-testid="asset-picker-confirm"
              className="v-btn-primary v-btn-sm"
              disabled={multiSel.length === 0}
              onClick={() => {
                onPick(list.filter((a) => multiSel.includes(a.id)));
                onOpenChange(false);
              }}
            >
              AÃ±adir {multiSel.length > 0 ? multiSel.length : ""}
            </button>
          </div>
        )}
        {!multi && confirmLabel && (
          <div className="flex items-center justify-between border-t border-[#303030] pt-3">
            <span className="truncate max-w-[50%] text-xs text-[#a3a3a3]">
              {singleSel ? list.find((a) => a.id === singleSel)?.label || "" : ""}
            </span>
            <button
              data-testid="asset-picker-assign-btn"
              className="v-btn-primary v-btn-sm"
              disabled={!singleSel}
              onClick={() => {
                const chosen = list.find((a) => a.id === singleSel);
                if (chosen) {
                  onPick(chosen);
                  onOpenChange(false);
                }
              }}
            >
              {confirmLabel}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AssetPicker;
